"use client"

/**
 * Offer-settle payment surface. Thin wrapper around the shared <PaymentSheet>
 * shell — owns offer-specific domain gates (deadline-expired, not-buyer,
 * status != accepted, already settled) and the offer/current-state + detail
 * pre-fetch. The shell handles listing-meta + checkout form + retry-settle UI.
 *
 * @see components/checkout/payment-sheet.tsx — shared shell
 * @see lib/offers/offer-builders.ts — settle builder (canonical)
 */

import { useCallback, useEffect, useMemo, useState } from "react"
import { asRawImageRef } from "@/lib/storage/public-url"
import { useTranslations } from "next-intl"
import { AlertTriangle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useOffer } from "@/hooks/use-offer"
import { logger } from "@/lib/logger"
import { settleOffer as settleOfferBuilder } from "@/lib/offers/offer-builders"
import type { OfferRow, OfferProfile, OfferListing } from "@/lib/offers/offer-types"
import {
  PaymentSheet,
  type PaymentSheetGate,
  type PaymentSheetVariant,
} from "@/components/checkout/payment-sheet"

const log = logger.child({ component: "offer-checkout-sheet" })

interface OfferCheckoutSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  offerId: string | null
  userId: string | null
}

interface CurrentStateResponse {
  offer: OfferRow
  existingOrderId: string | null
  deadlineExpired: boolean
}

interface OfferDetailResponse {
  offer: OfferRow & {
    listing: OfferListing | null
    seller: OfferProfile | null
    buyer: OfferProfile | null
  }
}

export function OfferCheckoutSheet({ open, onOpenChange, offerId, userId }: OfferCheckoutSheetProps) {
  const t = useTranslations()

  const [isPreparing, setIsPreparing] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [state, setState] = useState<CurrentStateResponse | null>(null)
  const [detail, setDetail] = useState<OfferDetailResponse | null>(null)
  const [deadlineExpired, setDeadlineExpired] = useState(false)
  const [initialPendingOrderId, setInitialPendingOrderId] = useState<string | null>(null)

  const offerSlot = useOffer(open ? offerId : null)
  const realtimeStatus = offerSlot?.status ?? null

  const offer = state?.offer ?? detail?.offer ?? null
  const listing = detail?.offer.listing ?? null
  const seller = detail?.offer.seller ?? null

  const status = realtimeStatus ?? offer?.status ?? null
  const isSettled = (offerSlot?.settledAt ?? offer?.settled_at) != null
  const isBuyer = userId != null && offer?.buyer_id === userId
  const currency = (offer?.currency ?? listing?.currency ?? "EUR") as string

  useEffect(() => {
    if (!open || !offerId) return
    let cancelled = false
    setIsPreparing(true)
    setError(null)
    setState(null)
    setDetail(null)
    setDeadlineExpired(false)
    setInitialPendingOrderId(null)

    Promise.all([
      fetch(`/api/offers/${encodeURIComponent(offerId)}/current-state`, { credentials: "include" }).then(
        (r) => r.json(),
      ),
      fetch(`/api/offers/${encodeURIComponent(offerId)}`, { credentials: "include" }).then((r) => r.json()),
    ])
      .then(([cs, det]) => {
        if (cancelled) return
        if (cs?.error) {
          setError(cs.error)
          return
        }
        const csTyped = cs as CurrentStateResponse
        const detTyped = det as OfferDetailResponse | { error?: string }

        setState(csTyped)
        if (!("error" in detTyped) || !detTyped.error) {
          setDetail(detTyped as OfferDetailResponse)
        }

        if (csTyped.deadlineExpired) {
          setDeadlineExpired(true)
          return
        }
        if (csTyped.existingOrderId) {
          setInitialPendingOrderId(csTyped.existingOrderId)
          return
        }

        const detailedOffer = (detTyped as OfferDetailResponse).offer
        if (!detailedOffer?.listing) {
          setError("Listing unavailable")
        }
      })
      .catch((e) => {
        if (cancelled) return
        setError(e instanceof Error ? e.message : "Network error")
      })
      .finally(() => {
        if (!cancelled) setIsPreparing(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, offerId])

  const close = useCallback(() => onOpenChange(false), [onOpenChange])

  const settleOffer = useCallback(
    async (orderId: string): Promise<boolean> => {
      if (!offerId) return false
      try {
        const r = await settleOfferBuilder({ offerId, orderId })
        if (!r.ok) {
          const err = (r.data ?? {}) as { error?: string }
          if (
            r.status === 409 &&
            (err.error === "Offer already settled" ||
              err.error === "Offer already settled or status changed")
          ) {
            return true
          }
          if (r.status === 409 && err.error === "Payment deadline expired") {
            setDeadlineExpired(true)
          }
          log.warn({ event: "offer_settle_failed", offerId, err: err.error }, "settle fallback failed")
          return false
        }
        return true
      } catch (err) {
        log.warn({ event: "offer_settle_threw", offerId, err }, "settle fallback threw")
        return false
      }
    },
    [offerId],
  )

  // Per-area gate evaluation. Order matches the previous file's IIFE switch:
  // deadline-expired → loading → fetch-error → not-buyer → not-accepted →
  // already-settled. The "merchant-not-ready" case is owned by the shared
  // shell (it's a function of meta.availableProviders, not of offer domain).
  const gate: PaymentSheetGate = useMemo(() => {
    if (deadlineExpired) {
      return {
        kind: "blocked",
        body: (
          <div className="flex flex-col items-center justify-center py-12 text-center px-4">
            <AlertTriangle className="h-10 w-10 text-red-500 mb-3" />
            <p className="text-base font-semibold">
              {t("offers.checkout.deadlineExpired", { default: "Payment deadline expired" })}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {t("offers.checkout.deadlineExpiredHint", {
                default: "The payment window for this offer has closed.",
              })}
            </p>
            {initialPendingOrderId && (
              <p className="text-xs text-muted-foreground/70 mt-3 font-mono select-all">
                Order ID: {initialPendingOrderId}
              </p>
            )}
            <Button variant="ghost" className="mt-4" onClick={close}>
              {t("common.close", { default: "Close" })}
            </Button>
          </div>
        ),
      }
    }

    if (isPreparing) return { kind: "loading" }

    if (error) {
      return {
        kind: "blocked",
        body: (
          <div className="flex flex-col items-center justify-center py-12 text-center px-4">
            <AlertTriangle className="h-10 w-10 text-red-500 mb-3" />
            <p className="text-base font-semibold">
              {t("offers.checkout.errorTitle", { default: "Something went wrong" })}
            </p>
            <p className="text-sm text-muted-foreground mt-2">{error}</p>
            <Button variant="ghost" className="mt-4" onClick={close}>
              {t("common.close", { default: "Close" })}
            </Button>
          </div>
        ),
      }
    }

    if (!offer || !listing) return { kind: "loading" }

    if (!isBuyer) {
      return {
        kind: "blocked",
        body: (
          <div className="flex flex-col items-center justify-center py-12 text-center px-4">
            <p className="text-base font-semibold">
              {t("offers.checkout.notBuyer", { default: "Not available" })}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {t("offers.checkout.notBuyerHint", {
                default: "Only the buyer can complete payment for this offer.",
              })}
            </p>
            <Button variant="ghost" className="mt-4" onClick={close}>
              {t("common.close", { default: "Close" })}
            </Button>
          </div>
        ),
      }
    }

    if (status !== "accepted") {
      return {
        kind: "blocked",
        body: (
          <div className="flex flex-col items-center justify-center py-12 text-center px-4">
            <p className="text-base font-semibold">
              {t("offers.checkout.notAccepted", { default: "Not accepted yet" })}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {t("offers.checkout.notAcceptedHint", {
                default: "This offer hasn't been accepted yet, or it's no longer pending.",
              })}
            </p>
            <Button variant="ghost" className="mt-4" onClick={close}>
              {t("common.close", { default: "Close" })}
            </Button>
          </div>
        ),
      }
    }

    if (isSettled) {
      return {
        kind: "blocked",
        body: (
          <div className="flex flex-col items-center justify-center py-12 text-center px-4">
            <p className="text-base font-semibold">
              {t("offers.checkout.alreadyPaid", { default: "Already paid" })}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {t("offers.checkout.alreadyPaidHint", { default: "This offer has been settled." })}
            </p>
            <Button variant="ghost" className="mt-4" onClick={close}>
              {t("common.close", { default: "Close" })}
            </Button>
          </div>
        ),
      }
    }

    return { kind: "ready" }
  }, [deadlineExpired, isPreparing, error, offer, listing, isBuyer, status, isSettled, initialPendingOrderId, t, close])

  // Variant built only when offer + listing are available (gate.ready guards
  // the meta fetch; we still need the variant constructed unconditionally
  // because hooks can't be conditional). When data is missing we feed a
  // minimal placeholder — the shell never reads it under loading/blocked.
  const variant: PaymentSheetVariant = useMemo(() => {
    if (!offer || !listing || !offerId) {
      // Placeholder variant — never reached by shell render paths because
      // gate stays in loading/blocked until offer+listing arrive.
      return {
        type: "offer",
        offerId: offerId ?? "pending",
        userId,
        sellerId: offer?.seller_id ?? "",
        currency,
        item: {
          id: `offer-settle-${offerId ?? "pending"}`,
          listingId: listing?.id ?? "",
          title: listing?.title ?? "",
          images: listing?.image_urls ?? [],
          sellerName: seller?.username ?? "seller",
          sellerAvatarUrl: asRawImageRef(seller?.avatar_url) ?? undefined,
          amount: offer?.amount ?? 0,
        },
      }
    }
    return {
      type: "offer",
      offerId,
      userId,
      sellerId: offer.seller_id,
      currency,
      item: {
        id: `offer-settle-${offer.id}`,
        listingId: listing.id,
        title: listing.title,
        images: listing.image_urls,
        sellerName: seller?.username ?? "seller",
        sellerAvatarUrl: asRawImageRef(seller?.avatar_url) ?? undefined,
        amount: offer.amount,
      },
    }
  }, [offer, listing, seller, offerId, userId, currency])

  return (
    <PaymentSheet
      open={open}
      onOpenChange={onOpenChange}
      variant={variant}
      gate={gate}
      title={t("offers.checkout.title", { default: "Complete offer payment" })}
      onSettle={settleOffer}
      initialPendingOrderId={initialPendingOrderId}
      shippingLabel={t("offers.checkout.shippingMethod", { default: "Shipping method" })}
      retryCopy={{
        title: t("offers.checkout.paymentReceivedTitle", { default: "Payment received" }),
        description: t("offers.checkout.paymentReceivedHint", {
          default:
            "Your payment was captured but we couldn't finalize the settlement. Please retry or contact support.",
        }),
        retryLabel: t("offers.checkout.retrySettlement", { default: "Retry Settlement" }),
        retryingLabel: t("offers.checkout.retrying", { default: "Retrying…" }),
        closeLabel: t("common.close", { default: "Close" }),
      }}
      merchantCopy={{
        title: t("offers.checkout.merchantNotReadyTitle", { default: "Payment not available" }),
        description: t("offers.checkout.merchantNotReadyHint", {
          default:
            "The seller's payment account is not ready. Please contact the seller or try again later.",
        }),
        closeLabel: t("common.close", { default: "Close" }),
        detail: t("offers.checkout.merchantNotReady", {
          default: "Seller cannot receive payments yet.",
        }),
      }}
    />
  )
}
